import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, TextField, Typography } from '@material-ui/core';
import { CustomButton, CustomVacantButton } from '../button';
import tokens from '../theme/tokens';

// 법인카드 등록 모달 — 카드사 · 카드번호 · 유효기간 · 명의. 서버는 끝 4자리만 보관한다(전체 번호 저장 안 함)
const CARD_COMPANIES = ['신한', '삼성', '현대', 'KB국민', '롯데', '하나', '우리', 'NH농협', 'BC', '씨티', '카카오뱅크', '토스', '기타'];

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 440,
    maxWidth: 'calc(100% - 32px)',
    padding: '32px 32px 28px',
    borderRadius: tokens.radiusLg,
    boxShadow: tokens.dialogShadow,
    [theme.breakpoints.down('sm')]: { padding: '28px 20px 24px' },
  },
  title: { fontSize: 22, fontWeight: 700, letterSpacing: -1, color: tokens.ink },
  fields: {
    marginTop: 18,
    '& .MuiInputBase-root': {
      boxShadow: 'none', marginBottom: 12, width: '100%', padding: '0 16px', borderRadius: tokens.radius,
      border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface,
      '& input': { height: 50, '&::-webkit-input-placeholder': { color: tokens.placeholder } },
      '& .MuiOutlinedInput-input': { padding: 0 },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: `${tokens.radius}px !important` },
  },
  row: { display: 'flex', gap: 10, '& > *': { flex: '1 1 0' } },
  select: {
    width: '100%', height: 50, marginBottom: 12, padding: '0 16px', borderRadius: tokens.radius,
    border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, fontSize: 15, fontFamily: 'inherit', color: tokens.ink, outline: 'none',
  },
  note: { fontSize: 12, color: tokens.muted, letterSpacing: -0.4, marginTop: 2 },
  error: { fontSize: 13, color: tokens.primaryDark, marginTop: 4 },
  foot: { display: 'flex', gap: 10, marginTop: 16, '& > *': { flex: '1 1 0', height: 48, fontSize: 16 } },
}));

const formatCard = (v) => String(v || '').replace(/\D/g, '').slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ');

function CorporateCardDialog({ open, onClose, onSave }) {
  const cs = useStyles();
  const [form, setForm] = useState({ company: CARD_COMPANIES[0], number: '', expMonth: '', expYear: '', holder: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'number' ? formatCard(e.target.value) : e.target.value }));
  const save = async () => {
    if (busy) return;
    const digits = form.number.replace(/\D/g, '');
    if (digits.length < 15) { setError('카드번호를 확인해 주세요.'); return; }
    if (!/^(0[1-9]|1[0-2])$/.test(form.expMonth.padStart(2, '0')) || form.expYear.replace(/\D/g, '').length !== 2) { setError('유효기간(MM/YY)을 확인해 주세요.'); return; }
    setBusy(true); setError('');
    const r = await onSave({ ...form, number: digits, expMonth: form.expMonth.padStart(2, '0') });
    setBusy(false);
    if (!r || !r.ok) { setError((r && r.message) || '등록하지 못했습니다.'); return; }
    setForm({ company: CARD_COMPANIES[0], number: '', expMonth: '', expYear: '', holder: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: cs.paper }} aria-labelledby="corporate-card-title">
      <Typography id="corporate-card-title" className={cs.title}>법인카드 등록</Typography>
      <div className={cs.fields}>
        <select className={cs.select} value={form.company} onChange={set('company')} aria-label="카드사">
          {CARD_COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <TextField fullWidth variant="outlined" placeholder="카드번호" value={form.number} inputProps={{ inputMode: 'numeric', autoComplete: 'cc-number', maxLength: 19 }} onChange={set('number')} />
        <div className={cs.row}>
          <TextField variant="outlined" placeholder="MM" value={form.expMonth} inputProps={{ inputMode: 'numeric', maxLength: 2, autoComplete: 'cc-exp-month' }} onChange={set('expMonth')} />
          <TextField variant="outlined" placeholder="YY" value={form.expYear} inputProps={{ inputMode: 'numeric', maxLength: 2, autoComplete: 'cc-exp-year' }} onChange={set('expYear')} />
        </div>
        <TextField fullWidth variant="outlined" placeholder="카드 명의(법인명)" value={form.holder} inputProps={{ autoComplete: 'cc-name', maxLength: 40 }} onChange={set('holder')} />
        <Typography className={cs.note}>카드번호는 끝 4자리만 저장됩니다.</Typography>
        {error && <Typography className={cs.error}>{error}</Typography>}
      </div>
      <div className={cs.foot}>
        <CustomVacantButton variant="contained" color="primary" onClick={onClose}>닫기</CustomVacantButton>
        <CustomButton variant="contained" color="primary" disabled={busy} onClick={save}>{busy ? '등록 중' : '등록'}</CustomButton>
      </div>
    </Dialog>
  );
}

export default CorporateCardDialog;
